import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Shield } from 'lucide-react';
import { Button } from './ui/button';
import NotificationDropdown from './NotificationDropdown';
import { isChallengeOnlyUser, getChallengeRedirectPath } from './ProtectedRoute';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [location.pathname]);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('session_token');
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  // Cuando el usuario hace click en el logo, ir al dashboard/challenge según acceso
  const handleLogoClick = (e) => {
    e.preventDefault();
    if (user) {
      if (isChallengeOnlyUser(user)) {
        navigate(getChallengeRedirectPath(user));
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/');
    }
  };

  const isActive = (path) => location.pathname === path;

  const challengeOnly = isChallengeOnlyUser(user);

  const navLinks = challengeOnly
    ? [
        { path: '/my-challenges', label: 'Mi Challenge' },
        { path: '/challenges', label: 'Challenges' },
      ]
    : [
        { path: '/academia', label: 'Academia' },
        { path: '/programs', label: 'Club VIP' },
        { path: '/challenges', label: 'Challenges' },
        { path: '/coaching', label: 'Coaching 1-1' },
        { path: '/mindset', label: 'Mindset' },
        { path: '/ebooks', label: 'E-Books' },
      ];

  return (
    <nav className="bg-[#09090b] border-b border-zinc-800 sticky top-0 z-[60]">
      <div className="max-w-[1600px] mx-auto px-2 md:px-6">
        <div className="flex justify-between items-center h-14 md:h-16">
          {/* Logo */}
          <a href="#" onClick={handleLogoClick} className="flex items-center cursor-pointer">
            <span className="font-barlow text-sm md:text-xl font-bold text-white tracking-tight">
              Booty<span className="text-pink-500">&</span>Legs Academy
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-2 lg:px-3 py-2 rounded-lg font-medium text-xs lg:text-sm transition-colors whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-pink-500 bg-pink-500/10'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center space-x-3">
            {user ? (
              <>
                {/* Notifications Bell */}
                <NotificationDropdown />
                
                {user.role === 'admin' && (
                  <Link to="/admin">
                    <Button 
                      className="bg-pink-600 hover:bg-pink-500 text-white font-bold text-sm px-4 py-2 rounded-lg"
                      data-testid="admin-link"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to={challengeOnly ? getChallengeRedirectPath(user) : '/dashboard'}>
                  <Button 
                    variant="ghost" 
                    className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg"
                    data-testid="dashboard-link"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {user.name}
                  </Button>
                </Link>
                <Button 
                  onClick={handleLogout} 
                  variant="ghost" 
                  className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg"
                    data-testid="login-button"
                  >
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/programs">
                  <Button 
                    className="bg-pink-600 hover:bg-pink-500 text-white font-bold rounded-full px-6"
                    data-testid="register-button"
                  >
                    Comenzar
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-white"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to="/login">
              <Button className="bg-pink-600 text-white text-xs px-3 py-1 rounded-full">
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-zinc-900 border-t border-zinc-800" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-pink-500 bg-pink-500/10'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            
            <div className="border-t border-zinc-800 my-4 pt-4">
              {user ? (
                <>
                  {/* Mobile Notifications */}
                  <div className="px-4 py-3 mb-2">
                    <NotificationDropdown />
                  </div>
                  
                  {user.role === 'admin' && (
                    <Link 
                      to="/admin" 
                      className="block px-4 py-3 rounded-lg text-pink-500 bg-pink-500/10 font-medium mb-2"
                      onClick={() => setIsOpen(false)}
                    >
                      <Shield className="w-4 h-4 inline mr-2" />
                      Panel Admin
                    </Link>
                  )}
                  <Link 
                    to={challengeOnly ? getChallengeRedirectPath(user) : '/dashboard'}
                    className="block px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    <User className="w-4 h-4 inline mr-2" />
                    {challengeOnly ? 'Mi Challenge' : 'Mi Dashboard'}
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setIsOpen(false); }} 
                    className="block w-full text-left px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium"
                  >
                    <LogOut className="w-4 h-4 inline mr-2" />
                    Cerrar Sesión
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="block px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    Iniciar Sesión
                  </Link>
                  <Link to="/programs" onClick={() => setIsOpen(false)}>
                    <Button className="w-full mt-2 bg-pink-600 hover:bg-pink-500 text-white rounded-full py-3 font-bold">
                      Comenzar Ahora
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
